// ─────────────────────────────────────────────────────────────────
// Glass-i — Interlayer Items (PVB / EVA / SGP) management page.
// Stage INTERLAYER-STOCK-1 — "just stock it" (Phase 1).
//
// A standalone, isolated master surface (mirrors the Off-cut register pattern)
// for defining interlayer FILM as consumable stock items. It does NOT touch the
// Item Master grid, Product Creation Studio, purchase, or proforma.
//
// An interlayer item = a normal inventory_items row (item_type='consumable',
// stocked in m²) that carries an item_interlayer_specs extension holding its
// type / thickness / colour / brand and the roll→m² (and kg→m²) CONVERSION —
// so you buy in rolls or kg and stock/consume in m². The Add/Edit modal makes
// two calls (create/update core item → upsert spec), exactly like the existing
// simple-create popups.
//
// Phase 1 is master-data only: it defines the items + conversion and shows a
// live "≈ m²/roll" preview. Receiving stock and consumption on lamination are
// later phases; balances are viewed on Inventory → Current Stock.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card, Table, Button, Space, Input, Select, InputNumber, Switch, Modal, Form,
  Tag, Tooltip, Typography, Alert, Popconfirm, message
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, BlockOutlined
} from '@ant-design/icons';

import useAuth from '../../../../hooks/useAuth';
import { itemsApi, studioLookups } from '../items/itemsApi';
import {
  sqmPerRoll, formatSqm, INTERLAYER_TYPE_OPTIONS
} from './interlayerConversion';
import './InterlayerItems.css';

const { Title, Text } = Typography;

const PERM = Object.freeze({
  VIEW:   'erp.masters.items.view',
  CREATE: 'erp.masters.items.create',
  UPDATE: 'erp.masters.items.update'
});
function hasPermission(me, key) {
  const list = (me && me.permissions) || [];
  return list.includes('*') || list.includes(key);
}
const typeLabel = (v) => (INTERLAYER_TYPE_OPTIONS.find((o) => o.value === v) || {}).label || (v ? String(v).toUpperCase() : '—');
const errMsg = (e, fb) => e?.response?.data?.error?.message || e?.message || fb;

// Live m²/roll preview — the signature figure of the modal. Reads the form's
// current roll size; the bold number is the one place the modal raises its voice.
function RollPreview({ form }) {
  Form.useWatch('rollWidthMm', form);
  Form.useWatch('rollLengthM', form);
  const per = sqmPerRoll(form.getFieldValue('rollWidthMm'), form.getFieldValue('rollLengthM'));
  if (per === null) return <span className="il-conv__figure il-conv__figure--empty">— m² / roll</span>;
  return (
    <span className="il-conv__figure">
      <b className="tabular-nums">{formatSqm(per)}</b> m² / roll
    </span>
  );
}

export default function InterlayerItemsPage() {
  const { me } = useAuth();
  const canCreate = hasPermission(me, PERM.CREATE);
  const canUpdate = hasPermission(me, PERM.UPDATE);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const searchTimer = useRef(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // list row or null
  const [saving, setSaving] = useState(false);
  const [uomOpts, setUomOpts] = useState([]);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await itemsApi.listInterlayers({
        page, pageSize,
        search: search || undefined,
        interlayerType: typeFilter || undefined
      });
      setRows((res && res.rows) || []);
      setTotal((res && res.total) || 0);
    } catch (e) {
      message.error(errMsg(e, 'Failed to load interlayer items.'));
    } finally { setLoading(false); }
  }, [page, pageSize, search, typeFilter]);

  useEffect(() => { load(); }, [load]);

  // UOM options for the (optional) stock unit — default to SQM.
  const loadUoms = useCallback(async () => {
    try {
      const list = await studioLookups.uoms({ q: '', limit: 100 });
      const opts = (Array.isArray(list) ? list : []).map((u) => ({
        value: u.id || u.uomId || u.value,
        label: u.uomCode || u.uom_code || u.label,
        code:  u.uomCode || u.uom_code || ''
      })).filter((o) => o.value);
      setUomOpts(opts);
      return opts;
    } catch { return []; }
  }, []);

  const openCreate = useCallback(async () => {
    setEditing(null);
    form.resetFields();
    const opts = await loadUoms();
    const sqm = opts.find((o) => String(o.code).toUpperCase() === 'SQM');
    form.setFieldsValue({ interlayerType: 'pvb', isAcoustic: false, isStructural: false, defaultUomId: sqm ? sqm.value : undefined });
    setModalOpen(true);
  }, [form, loadUoms]);

  const openEdit = useCallback(async (row) => {
    setEditing(row);
    form.resetFields();
    await loadUoms();
    // The list row has the core fields; fetch the full spec (remarks, flags).
    let spec = {};
    try { spec = (await itemsApi.getInterlayerSpec(row.itemId)) || {}; } catch { /* fall back to list row */ }
    form.setFieldsValue({
      itemName:        row.itemName,
      interlayerType:  spec.interlayerType || row.interlayerType || undefined,
      thicknessMm:     spec.thicknessMm ?? row.thicknessMm ?? null,
      color:           spec.color ?? row.color ?? '',
      brand:           spec.brand ?? row.brand ?? '',
      isAcoustic:      spec.isAcoustic ?? row.isAcoustic ?? false,
      isStructural:    spec.isStructural ?? row.isStructural ?? false,
      rollWidthMm:     spec.rollWidthMm ?? row.rollWidthMm ?? null,
      rollLengthM:     spec.rollLengthM ?? row.rollLengthM ?? null,
      kgPerSqm:        spec.kgPerSqm ?? row.kgPerSqm ?? null,
      shelfLifeMonths: spec.shelfLifeMonths ?? row.shelfLifeMonths ?? null,
      remarks:         spec.remarks ?? ''
    });
    setModalOpen(true);
  }, [form, loadUoms]);

  const submit = useCallback(async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const spec = {
        interlayerType:  v.interlayerType || null,
        thicknessMm:     v.thicknessMm ?? null,
        color:           v.color || null,
        brand:           v.brand || null,
        isAcoustic:      !!v.isAcoustic,
        isStructural:    !!v.isStructural,
        rollWidthMm:     v.rollWidthMm ?? null,
        rollLengthM:     v.rollLengthM ?? null,
        kgPerSqm:        v.kgPerSqm ?? null,
        shelfLifeMonths: v.shelfLifeMonths ?? null,
        remarks:         v.remarks || null
      };
      let itemId;
      if (editing) {
        itemId = editing.itemId;
        await itemsApi.update(itemId, { itemName: v.itemName, ...(v.defaultUomId ? { defaultUomId: v.defaultUomId } : {}) });
      } else {
        // Core consumable item — stocked in m². Minimal, purchasable, not sold
        // directly (it's a production input). Mirrors the simple-create flow.
        const core = {
          itemName:      v.itemName,
          itemType:      'consumable',
          isGoods:       true,
          isService:     false,
          isStockItem:   true,
          isPurchasable: true,
          isSellable:    false,
          isConsumable:  true
        };
        if (v.defaultUomId) core.defaultUomId = v.defaultUomId;
        const item = await itemsApi.create(core);
        itemId = item.id;
      }
      await itemsApi.upsertInterlayerSpec(itemId, spec);
      message.success(editing ? 'Interlayer item updated.' : 'Interlayer item created.');
      setModalOpen(false);
      await load();
    } catch (e) {
      message.error(errMsg(e, 'Save failed.'));
    } finally { setSaving(false); }
  }, [form, editing, load]);

  // "Remove" clears the interlayer spec only — it does NOT delete the item
  // (item delete is intentionally not exposed on the Item Master either).
  const removeSpec = useCallback(async (row) => {
    try {
      await itemsApi.deleteInterlayerSpec(row.itemId);
      message.success(`${row.itemName} is no longer marked as an interlayer.`);
      await load();
    } catch (e) { message.error(errMsg(e, 'Could not remove the interlayer spec.')); }
  }, [load]);

  const onSearch = useCallback((val) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); setSearch(val); }, 300);
  }, []);

  const columns = useMemo(() => ([
    { title: 'Item', dataIndex: 'itemName', key: 'itemName', ellipsis: true,
      render: (v, r) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
          <Tooltip title={v}><span style={{ fontWeight: 600 }}>{v}</span></Tooltip>
          <span style={{ fontSize: 10.5, color: '#94a3b8' }}>{r.itemCode}</span>
        </div>
      ) },
    { title: 'Type', dataIndex: 'interlayerType', key: 'interlayerType', width: 110,
      render: (v) => <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>{typeLabel(v)}</Tag> },
    { title: 'Thk (mm)', dataIndex: 'thicknessMm', key: 'thicknessMm', width: 84, align: 'right',
      render: (v) => (v != null ? <span className="tabular-nums">{v}</span> : <Text type="secondary">—</Text>) },
    { title: 'Colour', dataIndex: 'color', key: 'color', width: 110,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Brand', dataIndex: 'brand', key: 'brand', width: 120,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Roll (W×L)', key: 'roll', width: 140, align: 'right',
      render: (_, r) => (r.rollWidthMm && r.rollLengthM
        ? <span className="tabular-nums">{r.rollWidthMm} × {r.rollLengthM} m</span>
        : <Text type="secondary">—</Text>) },
    { title: 'm²/roll', dataIndex: 'sqmPerRoll', key: 'sqmPerRoll', width: 100, align: 'right',
      render: (v) => (v != null ? <span className="tabular-nums" style={{ fontWeight: 600 }}>{formatSqm(v)}</span> : <Text type="secondary">—</Text>) },
    { title: 'Stock UOM', dataIndex: 'uom', key: 'uom', width: 96,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Actions', key: 'actions', width: 96, fixed: 'right',
      render: (_, r) => (
        <Space size={2}>
          {canUpdate ? (
            <Tooltip title="Edit"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip>
          ) : null}
          {canUpdate ? (
            <Popconfirm title="Remove interlayer spec?" description="The item stays; it just won't be listed here." okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => removeSpec(r)}>
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          ) : null}
        </Space>
      ) }
  ]), [canUpdate, openEdit, removeSpec]);

  return (
    <div className="p-4 interlayer-page">
      <div className="flex items-center justify-between mb-3">
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <BlockOutlined style={{ marginRight: 8 }} />Interlayer Items
          </Title>
          <Text type="secondary">PVB / EVA / SGP films — stocked in m², bought in rolls or kg.</Text>
        </div>
        <Space>
          <Input.Search allowClear placeholder="Search name / code / brand" style={{ width: 240 }} onChange={(e) => onSearch(e.target.value)} />
          <Select allowClear placeholder="All types" style={{ width: 140 }} value={typeFilter}
            onChange={(v) => { setTypeFilter(v || undefined); setPage(1); }} options={INTERLAYER_TYPE_OPTIONS} />
          <Button icon={<ReloadOutlined />} onClick={load} />
          {canCreate ? <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add interlayer</Button> : null}
        </Space>
      </div>

      <Alert type="info" showIcon className="mb-3"
        message="Master data only"
        description="Define each interlayer film and its roll→m² conversion here. Receive stock through Purchase; view balances on Inventory → Current Stock." />

      <Card size="small" className="interlayer-grid" bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="itemId"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          sticky
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page, pageSize, total, showSizeChanger: true, size: 'small',
            pageSizeOptions: ['25', '50', '100'],
            onChange: (p, ps) => { if (ps !== pageSize) { setPageSize(ps); setPage(1); } else { setPage(p); } },
            showTotal: (t) => `${t} interlayer item${t === 1 ? '' : 's'}`
          }}
          locale={{ emptyText: loading ? ' ' : 'No interlayer items yet — add your first PVB / EVA / SGP film.' }}
        />
      </Card>

      <Modal
        title={editing ? `Edit interlayer · ${editing.itemName}` : 'Add interlayer item'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        okText={editing ? 'Save' : 'Create'}
        confirmLoading={saving}
        destroyOnClose
        width={560}
        className="interlayer-modal"
      >
        <Form form={form} layout="vertical" requiredMark="optional" size="small" className="il-form">
          <Form.Item name="itemName" label="Item name" rules={[{ required: true, message: 'Give the film a name' }]}>
            <Input maxLength={200} placeholder="e.g. 0.76mm Clear PVB (Trosifol)" />
          </Form.Item>

          {/* Film attributes — one dense row */}
          <div className="grid grid-cols-12 gap-x-3">
            <Form.Item className="col-span-3" name="interlayerType" label="Type" rules={[{ required: true, message: 'Required' }]}>
              <Select options={INTERLAYER_TYPE_OPTIONS} />
            </Form.Item>
            <Form.Item className="col-span-3" name="thicknessMm" label="Thk (mm)" tooltip="e.g. 0.38 / 0.76 / 1.14 / 1.52"
              rules={[
                { required: true, message: 'Thickness is required' },
                { type: 'number', min: 0.01, message: 'Must be greater than 0' }
              ]}>
              <InputNumber min={0} step={0.38} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item className="col-span-3" name="color" label="Colour">
              <Input maxLength={80} placeholder="clear" />
            </Form.Item>
            <Form.Item className="col-span-3" name="brand" label="Brand">
              <Input maxLength={80} placeholder="Trosifol" />
            </Form.Item>
          </div>

          {/* Boolean flags — compact inline toggle chips */}
          <div className="il-flags">
            <div className="il-switch">
              <span>Acoustic</span>
              <Form.Item name="isAcoustic" valuePropName="checked" noStyle>
                <Switch size="small" />
              </Form.Item>
            </div>
            <div className="il-switch">
              <span>Structural (SGP)</span>
              <Form.Item name="isStructural" valuePropName="checked" noStyle>
                <Switch size="small" />
              </Form.Item>
            </div>
          </div>

          {/* Signature — the rolls → m² conversion, with the live figure as anchor */}
          <div className="il-conv">
            <div className="il-conv__head">
              <span className="il-conv__title">Roll → m² conversion</span>
              <RollPreview form={form} />
            </div>
            <div className="grid grid-cols-3 gap-x-3">
              <Form.Item name="rollWidthMm" label="Width (mm)">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="2100" />
              </Form.Item>
              <Form.Item name="rollLengthM" label="Length (m)">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="200" />
              </Form.Item>
              <Form.Item name="kgPerSqm" label="kg / m²" tooltip="Used when a supplier bills by weight">
                <InputNumber min={0} step={0.05} style={{ width: '100%' }} placeholder="0.85" />
              </Form.Item>
            </div>
          </div>

          {/* Stocking */}
          <div className="grid grid-cols-12 gap-x-3">
            <Form.Item className="col-span-6" name="defaultUomId" label="Stock UOM" tooltip="Stock is held in area — default SQM">
              <Select allowClear showSearch optionFilterProp="label" placeholder="SQM"
                options={uomOpts.map((o) => ({ value: o.value, label: o.label }))} />
            </Form.Item>
            <Form.Item className="col-span-6" name="shelfLifeMonths" label="Shelf life (months)" tooltip="PVB is moisture-sensitive; stored now for future expiry alerts">
              <InputNumber min={0} step={1} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <Form.Item name="remarks" label="Remarks" className="il-remarks">
            <Input.TextArea rows={2} maxLength={2000} placeholder="Optional notes" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
